import React from "react";
import { getInitials } from "@utils/getInitial";
import { capitalizeFirstLetter } from "@utils/string";

export interface MemberCardData {
	id?: number;
	uuid?: string;
	name: string;
	email?: string;
	phoneNumber?: string;
	grade?: string | number;
	experience?: string;
	location?: string;
	technology?: string;
	assignedCourses?: string;
	assignedLabs?: string;
	role?: "convener" | "member";
	isConvener?: boolean;
	profilePicture?: string | null;
	memberType?: string;
}

const hasDisplayValue = (value?: string | number | null): boolean =>
	value !== undefined && value !== null && String(value).trim() !== "" && String(value) !== "N/A";

const formatMemberType = (memberType?: string): string | undefined => {
	if (!memberType || memberType === "N/A") {
		return undefined;
	}

	return capitalizeFirstLetter(memberType.replace(/_/g, " "));
};

interface MemberDetailsProps {
	member: MemberCardData;
	showEmail?: boolean;
	showPhoneNumber?: boolean;
	showGrade?: boolean;
	showExperience?: boolean;
	showLocation?: boolean;
	showTechnology?: boolean;
	showAssignedCourses?: boolean;
	showAssignedLabs?: boolean;
	showMemberType?: boolean;
	className?: string;
}

// Reusable Member Details component - just the info fields
export const MemberDetails: React.FC<MemberDetailsProps> = ({
	member,
	showEmail = true,
	showPhoneNumber = true,
	showGrade = true,
	showExperience = true,
	showLocation = true,
	showTechnology = true,
	showAssignedCourses = true,
	showAssignedLabs = false,
	showMemberType = true,
	className = "",
}) => {
	const formattedMemberType = formatMemberType(member.memberType);

	return (
		<div className={`space-y-1 text-sm text-gray-600 ${className}`}>
			{showEmail && member.email && member.email !== "N/A" && (
				<div>
					<span className="font-semibold">Email:</span>
					<span className="ml-1">{member.email}</span>
				</div>
			)}
			{showPhoneNumber && member.phoneNumber && member.phoneNumber !== "N/A" && (
				<div>
					<span className="font-semibold">Phone Number:</span>
					<span className="ml-1">{member.phoneNumber}</span>
				</div>
			)}
			{showGrade && (
				<div>
					<span className="font-semibold">Grade:</span>
					<span className="ml-1">{member.grade || "N/A"}</span>
				</div>
			)}
			{showMemberType && formattedMemberType && (
				<div>
					<span className="font-semibold">Member Type:</span>
					<span className="ml-1">{formattedMemberType}</span>
				</div>
			)}
			{showExperience && (
				<div>
					<span className="font-semibold">Experience:</span>
					<span className="ml-1">{member.experience || "N/A"}</span>
				</div>
			)}
			{showLocation && member.location && member.location !== "N/A" && (
				<div>
					<span className="font-semibold">Location:</span>
					<span className="ml-1">{member.location}</span>
				</div>
			)}
			{showTechnology && hasDisplayValue(member.technology) && (
				<div>
					<span className="font-semibold">Technology/Trade:</span>
					<span className="ml-1">{member.technology}</span>
				</div>
			)}
			{showAssignedCourses && hasDisplayValue(member.assignedCourses) && (
				<div>
					<span className="font-semibold text-primary">Assigned Courses:</span>
					<span className="ml-1">{member.assignedCourses}</span>
				</div>
			)}
			{showAssignedLabs && hasDisplayValue(member.assignedLabs) && (
				<div>
					<span className="font-semibold text-primary">Assigned Labs:</span>
					<span className="ml-1">{member.assignedLabs}</span>
				</div>
			)}
		</div>
	);
};

// Reusable Member Avatar component
export const MemberAvatar: React.FC<{
	name: string;
	profilePicture?: string | null;
	size?: "sm" | "md" | "lg";
	className?: string;
}> = ({ name, profilePicture, size = "md", className = "" }) => {
	const sizeClasses = {
		sm: "w-10 h-10 text-sm",
		md: "w-12 h-12 text-sm",
		lg: "w-14 h-14 text-base",
	};

	return (
		<div className={`rounded-full flex items-center justify-center flex-shrink-0 ${sizeClasses[size]} ${className}`}>
			{profilePicture ? (
				<img
					src={profilePicture}
					alt={`${name} profile`}
					className={`${sizeClasses[size]} rounded-full object-cover`}
				/>
			) : (
				<div
					className={`${sizeClasses[size]} rounded-full bg-primary text-white flex items-center justify-center font-medium`}>
					{getInitials({ name: name || "N/A" })}
				</div>
			)}
		</div>
	);
};

interface MemberCardProps {
	member: MemberCardData;
	showEmail?: boolean;
	showPhoneNumber?: boolean;
	showGrade?: boolean;
	showExperience?: boolean;
	showLocation?: boolean;
	showTechnology?: boolean;
	showAssignedCourses?: boolean;
	showAssignedLabs?: boolean;
	showMemberType?: boolean;
	showRole?: boolean;
	className?: string;
}

const MemberCard: React.FC<MemberCardProps> = ({
	member,
	showEmail = true,
	showPhoneNumber = true,
	showGrade = true,
	showExperience = true,
	showLocation = true,
	showTechnology = true,
	showAssignedCourses = true,
	showAssignedLabs = false,
	showMemberType = true,
	showRole = true,
	className = "",
}) => {
	const isConvener = member.isConvener || member.role === "convener";

	return (
		<div className={`bg-white rounded-lg border border-gray-200 p-4 flex items-start gap-4 ${className}`}>
			{/* Profile Image */}
			<MemberAvatar name={member.name} profilePicture={member.profilePicture} size="md" />

			{/* Member Details */}
			<div className="flex-1 min-w-0">
				<div className="flex items-start justify-between mb-2">
					<h3 className="font-semibold text-gray-900 text-base">{member.name}</h3>
					{showRole && (
						<span className={`text-sm font-medium rounded-full ${isConvener ? "text-primary" : "text-gray-800"}`}>
							{isConvener ? "Convener" : "Member"}
						</span>
					)}
				</div>
				<MemberDetails
					member={member}
					showEmail={showEmail}
					showPhoneNumber={showPhoneNumber}
					showGrade={showGrade}
					showExperience={showExperience}
					showLocation={showLocation}
					showTechnology={showTechnology}
					showAssignedCourses={showAssignedCourses}
					showAssignedLabs={showAssignedLabs}
					showMemberType={showMemberType}
				/>
			</div>
		</div>
	);
};

export default MemberCard;
